/**
 * deployment-index.js
 */

import React from 'react';
import { useMediaQuery, IconButton } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import {
  useHistory,
  useParams,
} from "react-router-dom";
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { Types } from 'utils/constants';
import { makeLocation, makeDeploymentSectionPath, paths } from 'utils/routing';
import useRecordStore from 'contexts/record-store';
import { UserContext } from 'contexts/user/user-context';
import RouteLink from 'routing/route-link';
import AppBar from 'components/app-bar';
import DrawerSection from 'components/menu-drawer/section';
import ConfirmDialog from 'components/confirm-dialog';
import Icon from 'components/icon';
import Button from 'components/button';
import ReviewToggle from 'components/review-toggle';
import RejectionAlert from 'components/rejection-alert';
import ReviewInvalidationAlert from 'components/review-invalidation-alert';
import RecordFooter from 'components/record-footer';
import MobilePageTitle from 'components/menu-drawer/mobile-page-title';
import { DetectionOdorsProvider } from 'components/detection-odors/context/detection-odors-context';
import StepChip from '../helpers/step-chip';
import getWizardState from '../helpers/get-wizard-state';
import DeploymentEditForm from './deployment-edit-form';
import DeploymentGeneralView from '../sections/general/deployment-general-view';
import DeploymentGeneralEdit from '../sections/general/deployment-general-edit';
import DeploymentDetectionView from '../sections/detection/deployment-detection-view';
import DeploymentDetectionEdit from '../sections/detection/deployment-detection-edit';
import DeploymentSummaryView from '../sections/summary/deployment-summary-view';
import DeploymentSummaryEdit from '../sections/summary/deployment-summary-edit';
import PatrolNonSearchView from '../sections/patrol-non-search/patrol-non-search-view';
import PatrolNonSearchEdit from '../sections/patrol-non-search/patrol-non-search-edit';
import PatrolBuildingSearchView from '../sections/patrol-building-search/patrol-building-search-view';
import PatrolBuildingSearchEdit from '../sections/patrol-building-search/patrol-building-search-edit';
import PatrolEvidenceSearchView from '../sections/patrol-evidence-search/patrol-evidence-search-view';
import PatrolEvidenceSearchEdit from '../sections/patrol-evidence-search/patrol-evidence-search-edit';
import PatrolHumanSearchView from '../sections/patrol-human-search/patrol-human-search-view';
import PatrolHumanSearchEdit from '../sections/patrol-human-search/patrol-human-search-edit';
import PatrolTrackingView from '../sections/patrol-tracking/patrol-tracking-view';
import PatrolTrackingEdit from '../sections/patrol-tracking/patrol-tracking-edit';
import PatrolNewEdit from '../sections/patrol-new/patrol-new-edit';


const useStyles = makeStyles(theme => ({
  root: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    '& > .padded': {
      padding: `0 ${theme.spacing(3)}`,
      [theme.size.menuDrawer.mediaRuleMobileContent]: {
        padding: `0 ${theme.spacing(2)}`,
      },
      [theme.breakpoints.down('sm_md')]: {
        padding: `0 ${theme.spacing(.5)}`,
      }
    }
  },
  addPatrolType: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.spacing(3),
    paddingBottom: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(5)
    }
  },
  newPatrolTypeButton: {
    border: `2px dashed ${theme.palette.grey.colors[8]}`,
    backgroundColor: theme.palette.background.paper,
    zIndex: 1,
    marginTop: -5,
    marginBottom: theme.spacing(5),
    borderRadius: '0 0 4px 4px',
    '& > *': {
      width: '100%',
      padding: 17,
      borderRadius: '0 0 4px 4px',
    },
  },
  completeChip: {
    marginRight: theme.spacing(2),
  },
  reviewContainer: {
    marginTop: theme.spacing(3),
    justifyContent: 'flex-end',
    [theme.breakpoints.down('md')]: {
      marginTop: theme.spacing(2),
    },
    [theme.breakpoints.down('sm_md')]: {
      marginTop: theme.spacing(1),
    },
    [theme.breakpoints.down('sm')]: {
      margin: `${theme.spacing(3)} 0`
    }
  },
}));


// use wrapper to remount component when deployment ID changes
export default function DeploymentIndex(props) {
  const params = useParams();
  const deploymentId = parseInt(params.deploymentId) || 0;

  return <DeploymentIndexImplementation
    deploymentId={deploymentId}
    key={deploymentId}
    {...props}
  />;
}


function DeploymentIndexImplementation(props) {
  const {
    deploymentId,    
    drawerState,
    basePath,
    onClose,
  } = props;
  const classes = useStyles();
  const { user } = React.useContext(UserContext);
  const isMobileDrawer = useMediaQuery(theme => theme.breakpoints.down('sm_md'), { noSsr: true });
  const editorTranslation = 'deploymentEdit';
  const { t } = useTranslation('general');
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const params = useParams();
  const { viewSectionId, editSectionId } = params;
  const recordStore = useRecordStore();
  const deploymentRecord = recordStore.useRecord(Types.Record.DEPLOYMENT, deploymentId).ornull;
  const hasRecord = Boolean(deploymentRecord);
  const isComplete = hasRecord ? deploymentRecord.get().isComplete : false;
  const patrol = hasRecord ? deploymentRecord.get().patrol : null;
  const detection = hasRecord ? deploymentRecord.get().detection : null;
  const [doSaveGoToNewPatrolType, setDoSaveGoToNewPatrolType] = React.useState(()=> async ()=>{});
  const [editRecord, setEditRecord] = React.useState();
  const [options, setOptions] = React.useState();
  const [confirmDeletePatrolType, setConfirmDeletePatrolType] = React.useState();
  const [newPatrolTypeKey, setNewPatrolTypeKey] = React.useState();
  const isNewPatrolTypeRequest = Boolean(
    editSectionId === makeDeploymentSectionPath(Types.DeploymentSection.PATROL)
  );
  const numPatrolTypes = patrol?.patrolTypes?.length || 0;


  // when we have no view/edit section then determine where to go based on deployment state.
  // if incomplete then we auto-edit the first incomplete section.
  // if complete then we just view general.
  React.useEffect(() => {
    if (
      user.role.status.isActive
      && !user.role.isSupervisor 
      && hasRecord 
      && viewSectionId === undefined 
      && editSectionId === undefined
    ) {
      const wizardState = getWizardState(deploymentRecord.get(), null, basePath, history);
      if (wizardState.nextLocation) {
        history.replace(wizardState.nextLocation);
      } else if (deploymentRecord.get().review?.status !== Types.ReviewStatus.REJECTED) {
        // general
        history.replace(makeLocation(
          basePath,
          'deployment',
          'view',
          {
            deploymentId: deploymentRecord.get().id,
            viewSectionId: makeDeploymentSectionPath(Types.DeploymentSection.GENERAL),
          },
          history.location
        ));
      }
    } else if (user.role.isSupervisor && viewSectionId === undefined) {
      history.replace(makeLocation(
        basePath,
        'deployment',
        'view',
        {
          deploymentId: deploymentRecord.get().id,
          viewSectionId: 0
        },
        history.location
      ));
    }

  // deploymentRecord is hookstate, causes infinite loop
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasRecord, viewSectionId, editSectionId, basePath, history, user.role.isSupervisor]);

  // Check if deployment is "Not performed" based on fulfillmentState
  const isNotPerformed = hasRecord && (
    deploymentRecord.get().fulfillmentState === Types.FulfillmentState.DogNotDeployed || 
    deploymentRecord.get().fulfillmentState === Types.FulfillmentState.CancelledEnroute
  );

  // allow some render time before setting new patrol type editor key so we don't have
  // our editor component removed from the DOM too fast (causing no-op error).
  // NOTE: this key is required for hook form to properly reset to an empty patrol type
  // instead of re-using the previous state and causing all kinds of grief.
  React.useEffect(() => {
    setTimeout(() => setNewPatrolTypeKey(numPatrolTypes));
  }, [numPatrolTypes]);


  // delete patrol type
  const deletePatrolType = async (patrolTypeRecord) => {
    if (await confirmDeletePatrolType()) {
      try {
        drawerState.loading.content.set(true);
        const response = await axios.post(`/deployment/${deploymentId}/delete-patrol-type`, {
          type: patrolTypeRecord.type,
          id: patrolTypeRecord.id
        });
        const newDeployment = response.data;
        recordStore.updateRecord(newDeployment);
        setEditRecord(newDeployment);
        enqueueSnackbar('Patrol type deleted', {
          variant: 'success'
        });
      } catch {
        enqueueSnackbar('Error deleting patrol type', {
          variant: 'error',
          persist: true,
          anchorOrigin: {
            vertical: "top",
            horizontal: "center"
          }
        });
      } finally {
        drawerState.loading.content.set(false);
      }
    }
  }


  // make drawer section
  const makeDrawerSection = (label, sectionType, sectionRecord, viewComponent, editComponent, isSectionComplete, extraProps) => {
    const sectionPath = makeDeploymentSectionPath(sectionType, sectionRecord);
    const editRoutePath = basePath
      + paths.deployment.base
      + paths.deployment.edit.path.replace(':editSectionId', sectionPath);

    const chipState = sectionPath === editSectionId
      ? 'edit'
      : isSectionComplete
        ? 'complete'
        : 'incomplete';
    const stepChip = !isComplete
      ? <StepChip
        step={extraProps.step}
        state={chipState}
      />
      : <Icon type={chipState} className={`${classes.completeChip} ${classes[chipState]}`} />;

    return <>
      <DrawerSection
        deploymentId={deploymentId}
        deployment={deploymentRecord ? deploymentRecord.get() : null}
        dog={deploymentRecord ? deploymentRecord.get().dog : null}
        sectionType={sectionType}
        sectionPath={sectionPath}
        basePath={basePath}
        drawerState={drawerState}
        onClose={onClose}
        label={label}
        stepChip={stepChip}
        edit={{
          component: editComponent,
          record: editRecord,
          setRecord: setEditRecord,
          options,
          setOptions,
          translation: editorTranslation,
          routePath: editRoutePath,
          location: makeLocation(
            basePath,
            'deployment',
            'edit',
            {
              deploymentId,
              viewSectionId: sectionPath,
              editSectionId: sectionPath
            },
            history.location
          )
        }}
        routes={[
          {
            // use editor form wrapper for edit component. child components are specified in edit.component above
            component: DeploymentEditForm,
            path: [editRoutePath]
          },
          {
            component: viewComponent,
            path: [basePath + paths.deployment.base + paths.deployment.view.path]
          }
        ]}
        {...extraProps}
      />
      { // Annex for adding multiple new patrol types
      ( sectionType === Types.DeploymentSection.PATROL && extraProps.isAdd ) &&
        <div className={classes.newPatrolTypeButton}>
          <Button
            variant='text'
            color='primary'
            onClick={async () => await doSaveGoToNewPatrolType()}
          >
            + Save And Add New Patrol Type
          </Button>
        </div>
      }
    </>
  }


  // make patrol drawer section
  const makePatrolSection = (patrolTypeRecord, patrolIndex) => {
    let viewComponent = null;
    let editComponent = null;
    if (patrolTypeRecord.type === Types.Patrol.NONSEARCH) {
      viewComponent = PatrolNonSearchView;
      editComponent = PatrolNonSearchEdit;
    } else if (patrolTypeRecord.type === Types.Patrol.BUILDING_SEARCH) {
      viewComponent = PatrolBuildingSearchView;
      editComponent = PatrolBuildingSearchEdit;
    } else if (patrolTypeRecord.type === Types.Patrol.EVIDENCE_SEARCH) {
      viewComponent = PatrolEvidenceSearchView;
      editComponent = PatrolEvidenceSearchEdit;
    } else if (patrolTypeRecord.type === Types.Patrol.HUMAN_SEARCH) {
      viewComponent = PatrolHumanSearchView;
      editComponent = PatrolHumanSearchEdit;
    } else if (patrolTypeRecord.type === Types.Patrol.TRACKING) {
      viewComponent = PatrolTrackingView;
      editComponent = PatrolTrackingEdit;
    }

    const stepSuffix = isNewPatrolTypeRequest || patrol.patrolTypes.length > 1
      ? (patrolIndex + 10).toString(36).toLowerCase()
      : '';

    return makeDrawerSection(
      `${Types.Patrol.getName(t, patrolTypeRecord.type)}`,
      Types.DeploymentSection.PATROL,
      patrolTypeRecord,
      viewComponent,
      editComponent,
      true,
      {
        record: patrolTypeRecord,
        patrolIndex,
        step: `2${stepSuffix}`,
        onPatrolDelete: () => deletePatrolType(patrolTypeRecord)
      }
    );
  }


  const closeButton = (
    <IconButton
      data-testid="mobile-drawer-close-button"
      size="small"
      style={{color: 'inherit'}}
      onClick={onClose}
    >
      <Icon type='mobile-close-drawer' />
    </IconButton>
  );


  return (
    <>
      {(hasRecord || deploymentId === 0) &&
      <>
        {/* mobile header */}
        {isMobileDrawer &&
        <>
          <AppBar
            title="Deployment"
            rightActions={closeButton}
            recordGroupType={Types.RecordGroup.DEPLOYMENT}
          />
        </>}

        <DetectionOdorsProvider>
          <div className={classes.root}>
            {(isMobileDrawer && user.role.isSupervisor) && 
            <MobilePageTitle title={<>
              {deploymentRecord?.get().caseNumber}
              <span style={{ padding: '0px 6px' }}>•</span>
              {deploymentRecord?.get().handler.name}</>}
            />}

            <div className='padded'>

              {/* review toggle */}
              {isComplete && user.role.isSupervisor && 
              <div className={`flex-hc ${classes.reviewContainer}`}>
                {/* <IconButton
                  style={{ color: 'inherit', marginRight: 8 }}
                  component={Link}
                  href={`/Report/GenerateReport?OfficerId=${deploymentRecord.get().handler.id}&Exerciseid=${deploymentId}&DogId=${deploymentRecord.get().dog.id}&ReportType=6&IsNewReport=True`}
                >
                  <Icon type='report' />
                </IconButton> */}
                <ReviewToggle
                  type={Types.Record.DEPLOYMENT}
                  id={deploymentId}
                  drawerState={drawerState}
                />
              </div>}

              <RejectionAlert review={deploymentRecord?.get().review} style={{ marginTop: 24 }} />
              <ReviewInvalidationAlert review={deploymentRecord?.get().review} style={{ marginTop: 24 }} />

              {/* general */}
              {makeDrawerSection(
                'General',
                Types.DeploymentSection.GENERAL,
                null,
                DeploymentGeneralView,
                DeploymentGeneralEdit,
                deploymentId !== 0,
                {
                  step: '1',
                  firstSection: true
                },
              )}


              {/* step 2 & 3 section place-holders */}
              {deploymentId === 0 &&
              <>
                <DrawerSection
                  label={'Detection / Patrol'}
                  disabled
                  stepChip={<StepChip step='2' state='incomplete' />}
                  edit={{ routePath: '_' }}
                  routes={[]}
                />

                <DrawerSection
                  label={'Summary'}
                  disabled
                  stepChip={<StepChip step='3' state='incomplete' />}
                  edit={{ routePath: '_' }}
                  routes={[]}
                />
              </>}


              {/* detection */ }
              {deploymentId !== 0 && detection && !isNotPerformed && makeDrawerSection(
                'Detection',
                Types.DeploymentSection.DETECTION,
                null,
                DeploymentDetectionView,
                DeploymentDetectionEdit,
                true,
                {
                  step: '2'
                }
              )}


              {/* patrol types */}
              {deploymentId !== 0 && patrol && patrol.patrolTypes && !isNotPerformed && patrol.patrolTypes.map((patrolType, i) =>
              <React.Fragment key={i}>
                {makePatrolSection(patrolType, i)}
              </React.Fragment>)}


              {/* ADD patrol type button */}
              {Boolean(patrol) && !isNewPatrolTypeRequest && user.role.isHandler && !isNotPerformed &&
                <>
                  <div id={`section-${deploymentId}-${Types.DeploymentSection.BUTTONSCROLL}`}></div>
                  <div className={classes.addPatrolType}>
                    <Button
                      component={RouteLink}
                      variant='contained'
                      color='primary'
                      disabled={!user.role.status.isActive}
                      data-testid='add-patrol-type-button'
                      startIcon={<Icon type='add' />}
                      basePath={basePath}
                      pathSectionName='deployment'
                      pathItemName='edit'
                      pathParams={{
                        deploymentId,
                        viewSectionId: makeDeploymentSectionPath(Types.DeploymentSection.PATROL),
                        editSectionId: makeDeploymentSectionPath(Types.DeploymentSection.PATROL),
                      }}
                    >
                      Patrol Type
                    </Button>
                  </div>
                </>}


              {/* NEW patrol type section */}
              {isNewPatrolTypeRequest && !isNotPerformed && makeDrawerSection(
                `New Patrol Type`,
                Types.DeploymentSection.PATROL,
                null,
                null,
                PatrolNewEdit,
                null,
                {
                  patrolIndex: patrol?.patrolTypes?.length || 0,
                  key: newPatrolTypeKey,
                  isAdd: true,
                  step: `2${patrol?.patrolTypes?.length > 0
                    ? (patrol?.patrolTypes?.length + 10).toString(36).toLowerCase()
                    : ''}`,
                  setOnSaveManualTrigger: setDoSaveGoToNewPatrolType,
                },
              )}


              {/* summary */}
              {deploymentId !== 0 && makeDrawerSection(
                'Summary',
                Types.DeploymentSection.SUMMARY,
                null,
                DeploymentSummaryView,
                DeploymentSummaryEdit,
                hasRecord && deploymentRecord.get().summary.comments !== '',
                {
                  step: '3'
                }
              )}

              {deploymentId !== 0 && deploymentRecord &&
              <RecordFooter
                  createdBy={deploymentRecord.get().handler.name}
                  dateCreated={deploymentRecord.get().dateCreated}
                />}

              {/* extra scroll height */}
              <div style={{ height: 240 }} />

            </div>

          </div>
        </DetectionOdorsProvider>

        {/* delete patrol type confirm dialog */}
        <ConfirmDialog
          titleStyle="warning"
          title="Confirm Delete Patrol Type"
          titleIcon="warning"
          content="Are you sure you want to delete this patrol type?"
          setConfirm={setConfirmDeletePatrolType}
        />
      </>}
    </>
  );
}